/**
 * App-level sound manager (SPEC §5.6).
 *
 * Audio is **on by default** on every phone — the 10-second countdown chime and
 * the reveal sting are the point, and twelve phones landing the guillotine
 * together is the intended chaos. Nothing in the UI turns it off — the mute
 * flag survives here, persisted to localStorage, for the phone's own settings
 * and for tests, but no screen renders a control for it.
 *
 * Sounds are synthesised with the Web Audio API rather than shipped as files:
 * no network fetch to race the countdown, no bundle weight, no CORS, and it
 * degrades to silence anywhere AudioContext is unavailable (including jsdom,
 * so tests never need to stub it).
 *
 * Browsers block audio until a user gesture. `unlock()` is called from the
 * first tap anywhere in the app (see `app/Shell.tsx`), which in this game is
 * always long before the first sound needs to play.
 */

export type SoundName =
  /** The guillotine falling: fires when the 10s lock countdown starts. */
  | 'countdown'
  /** Per-second tick during the countdown. */
  | 'tick'
  /** The reveal sting, at the top of the ends-first flip. */
  | 'reveal'
  /** Small affirmative blip: a pick registered, a card placed. */
  | 'blip'
  /** Someone joined the lobby. */
  | 'join'
  /** Game start / round leaderboard fanfare. */
  | 'fanfare';

const STORAGE_KEY = 'idgame.muted';

interface Tone {
  /** Frequency in Hz at the start of the tone. */
  freq: number;
  /** Frequency to glide to, if different. */
  to?: number;
  /** Seconds from the start of the sound. */
  at: number;
  /** Seconds. */
  duration: number;
  type?: OscillatorType;
  gain?: number;
}

/** Each sound is a short list of scheduled tones. Cheap, and always in time. */
const SOUNDS: Readonly<Record<SoundName, readonly Tone[]>> = {
  countdown: [
    { freq: 880, at: 0, duration: 0.16, type: 'triangle', gain: 0.5 },
    { freq: 1320, at: 0.1, duration: 0.34, type: 'triangle', gain: 0.38 },
    { freq: 440, at: 0.14, duration: 0.5, type: 'sine', gain: 0.24 },
  ],
  tick: [{ freq: 1200, at: 0, duration: 0.045, type: 'square', gain: 0.16 }],
  reveal: [
    { freq: 330, to: 660, at: 0, duration: 0.22, type: 'sawtooth', gain: 0.24 },
    { freq: 660, at: 0.18, duration: 0.18, type: 'triangle', gain: 0.3 },
    { freq: 990, at: 0.3, duration: 0.45, type: 'triangle', gain: 0.26 },
  ],
  blip: [{ freq: 720, at: 0, duration: 0.06, type: 'sine', gain: 0.2 }],
  join: [
    { freq: 587, at: 0, duration: 0.09, type: 'sine', gain: 0.2 },
    { freq: 880, at: 0.07, duration: 0.14, type: 'sine', gain: 0.2 },
  ],
  fanfare: [
    { freq: 523, at: 0, duration: 0.12, type: 'triangle', gain: 0.26 },
    { freq: 659, at: 0.1, duration: 0.12, type: 'triangle', gain: 0.26 },
    { freq: 784, at: 0.2, duration: 0.12, type: 'triangle', gain: 0.26 },
    { freq: 1046, at: 0.3, duration: 0.4, type: 'triangle', gain: 0.3 },
  ],
};

type MuteListener = (muted: boolean) => void;

function readStoredMute(): boolean {
  try {
    return globalThis.localStorage?.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

class SoundManager {
  /** SPEC §5.6: on by default. Only an explicit toggle turns it off. */
  #muted = readStoredMute();
  #context: AudioContext | null = null;
  #master: GainNode | null = null;
  #listeners = new Set<MuteListener>();
  #unavailable = false;

  get muted(): boolean {
    return this.#muted;
  }

  /** True when this environment can make noise at all (false in jsdom/SSR). */
  get available(): boolean {
    return !this.#unavailable && typeof this.#ctor() === 'function';
  }

  setMuted(muted: boolean): void {
    if (this.#muted === muted) return;
    this.#muted = muted;
    try {
      globalThis.localStorage?.setItem(STORAGE_KEY, muted ? '1' : '0');
    } catch {
      /* private mode; the toggle still works for this session */
    }
    for (const listener of this.#listeners) listener(muted);
  }

  toggleMuted(): boolean {
    this.setMuted(!this.#muted);
    return this.#muted;
  }

  /** Subscribe to mute changes. Returns an unsubscribe function. */
  subscribe(listener: MuteListener): () => void {
    this.#listeners.add(listener);
    return () => {
      this.#listeners.delete(listener);
    };
  }

  /**
   * Resume the AudioContext from inside a user gesture. Safe to call on every
   * tap; it is a no-op once running.
   */
  unlock(): void {
    const ctx = this.#ensureContext();
    if (ctx && ctx.state === 'suspended') void ctx.resume().catch(() => undefined);
  }

  /** Play a sound now. Silent when muted or unavailable; never throws. */
  play(name: SoundName): void {
    if (this.#muted) return;
    const tones = SOUNDS[name];
    if (!tones) return;

    const ctx = this.#ensureContext();
    const master = this.#master;
    if (!ctx || !master) return;
    if (ctx.state === 'suspended') void ctx.resume().catch(() => undefined);

    const start = ctx.currentTime + 0.001;
    for (const tone of tones) {
      try {
        this.#scheduleTone(ctx, master, tone, start);
      } catch {
        /* one bad tone must never break a phase transition */
      }
    }
  }

  /**
   * Play a sound at a specific wall-clock time (ms epoch), for events the
   * server has scheduled — the reveal's `startAt`, for instance, so every phone
   * stings together. Times already past play immediately.
   */
  playAt(name: SoundName, whenEpochMs: number): void {
    const delay = whenEpochMs - Date.now();
    if (delay <= 0) {
      this.play(name);
      return;
    }
    globalThis.setTimeout(() => this.play(name), delay);
  }

  /** Tear down the context. Tests and hot-reload only. */
  reset(): void {
    void this.#context?.close().catch(() => undefined);
    this.#context = null;
    this.#master = null;
    this.#unavailable = false;
  }

  #scheduleTone(ctx: AudioContext, master: GainNode, tone: Tone, start: number): void {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const at = start + tone.at;
    const end = at + tone.duration;
    const peak = tone.gain ?? 0.25;

    osc.type = tone.type ?? 'sine';
    osc.frequency.setValueAtTime(tone.freq, at);
    if (tone.to !== undefined) osc.frequency.exponentialRampToValueAtTime(tone.to, end);

    gain.gain.setValueAtTime(0.0001, at);
    gain.gain.exponentialRampToValueAtTime(peak, at + Math.min(0.02, tone.duration / 2));
    gain.gain.exponentialRampToValueAtTime(0.0001, end);

    osc.connect(gain);
    gain.connect(master);
    osc.start(at);
    osc.stop(end + 0.02);
  }

  #ctor(): (new () => AudioContext) | undefined {
    const scope = globalThis as unknown as {
      AudioContext?: new () => AudioContext;
      webkitAudioContext?: new () => AudioContext;
    };
    return scope.AudioContext ?? scope.webkitAudioContext;
  }

  #ensureContext(): AudioContext | null {
    if (this.#context) return this.#context;
    if (this.#unavailable) return null;

    const Ctor = this.#ctor();
    if (typeof Ctor !== 'function') {
      this.#unavailable = true;
      return null;
    }
    try {
      const ctx = new Ctor();
      const master = ctx.createGain();
      master.gain.value = 0.7;
      master.connect(ctx.destination);
      this.#context = ctx;
      this.#master = master;
      return ctx;
    } catch {
      this.#unavailable = true;
      return null;
    }
  }
}

/**
 * The single app-wide sound manager. Import it anywhere:
 *
 * ```ts
 * import { sound } from '../../lib/audio.js';
 * sound.play('countdown');
 * sound.playAt('reveal', reveal.startAt);
 * ```
 */
export const sound = new SoundManager();
